/**
 * PDP cut calculator. Checkout pricing (new app) uses cart _cut_calculator_config plus
 * product metafields — not line properties. properties[_cut_calculator_*] stay on the
 * line for orders and older app versions that read eight line attributes.
 *
 * Calculator JSON: custom.cut_calculator holds shape_surcharges + fees only.
 * Shapes, width, elastic, and roll length come from product metafields (Liquid).
 * Elastic polynomial scale: market/shop custom.elastic_term_scale (default 1).
 * Keys: voksdugen-app/extensions/price-calculater/src/calculator-json.ts
 */
const SHAPE_PRICES_KEY_PREFIX = 'shape_prices__';

const FEE_JSON_KEYS = {
    widthAdjustment: 'width_adjustment',
    roundedCorners: 'rounded_corners',
    beveledEdges: 'beveled_edges',
};

const SHAPE_TO_JSON_KEY = {
    Rectangular: `${SHAPE_PRICES_KEY_PREFIX}rectangular`,
    Oval: `${SHAPE_PRICES_KEY_PREFIX}oval`,
    Ellipse: `${SHAPE_PRICES_KEY_PREFIX}ellipse`,
    Round: `${SHAPE_PRICES_KEY_PREFIX}round`,
    'Round with Elastic': `${SHAPE_PRICES_KEY_PREFIX}round_elastic`,
};

function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isMoneyObject(value) {
    return isPlainObject(value) && Object.prototype.hasOwnProperty.call(value, 'amount');
}

function parseShapeMoney(raw) {
    if (raw === undefined || raw === null || raw === '') {
        return null;
    }

    if (isMoneyObject(raw)) {
        return parseShapeMoney(raw.amount);
    }

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

function normalizeClientCalculatorJson(parsed) {
    if (!isPlainObject(parsed)) {
        return {};
    }

    const output = { ...parsed };

    if (isPlainObject(parsed.shape_surcharges)) {
        for (const [token, value] of Object.entries(parsed.shape_surcharges)) {
            output[`${SHAPE_PRICES_KEY_PREFIX}${token}`] = value;
        }
    }

    return output;
}

function shapePriceKeyCandidates(shape) {
    const snake = String(shape)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    const compact = snake.replace(/_with_/g, '_');
    const candidates = [
        SHAPE_TO_JSON_KEY[shape],
        `${SHAPE_PRICES_KEY_PREFIX}${snake}`,
        `${SHAPE_PRICES_KEY_PREFIX}${compact}`,
    ];

    return candidates.filter((key, index) => key && candidates.indexOf(key) === index);
}

function parseShapePricesPayload(raw) {
    if (raw == null || raw === '' || raw === 'null') {
        return {};
    }

    let parsed = raw;

    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return {};
        }
    }

    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return {};
        }
    }

    return normalizeClientCalculatorJson(parsed);
}

function readCalculatorJson(host) {
    const script = host.querySelector('script[type="application/json"][data-cut-calculator]');
    const raw = script
        ? script.textContent
        : host.getAttribute('data-cut-calculator');
    return parseShapePricesPayload(raw);
}

function lookupFeePrice(calculatorJson, feeKey) {
    const fees = calculatorJson?.fees;
    if (!isPlainObject(fees)) {
        return null;
    }

    const block = fees[feeKey];
    if (!isPlainObject(block)) {
        return null;
    }

    return parseShapeMoney(block.price);
}

function parseElasticTermScale(raw) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function lookupJsonShapePrice(shapePrices, shape) {
    if (!shapePrices) {
        return null;
    }

    for (const key of shapePriceKeyCandidates(shape)) {
        if (!Object.prototype.hasOwnProperty.call(shapePrices, key)) {
            continue;
        }

        const parsed = parseShapeMoney(shapePrices[key]);
        if (parsed !== null) {
            return parsed;
        }
    }

    return null;
}

class CutCalculator extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.dom = {
            shapeInputs: this.querySelectorAll('input.cut-calculator__shape-input'),
            inputFields: this.querySelectorAll('input.cut-calculator__input-field'),
            formInputs: this.querySelectorAll(`[form="${ this.dataset.formId }"]`),
            infoTriggers: this.querySelectorAll('[data-cut-calculator-info-trigger]'),

            price: document.querySelector(`.product-info__price .price__current`),
            compareAtPrice: document.querySelector(`.product-info__price .price__was`),
        }

        const calculatorJson = readCalculatorJson(this);

        this.settings = {
            basePrice: Number(this.dataset.price),
            baseCompareAtPrice: Number(this.dataset.compareAtPrice),
            currentShape: Array.from(this.dom.shapeInputs).find((input) => input.checked)?.value,
            width: Number(this.dataset.width),
            elasticTermScale: parseElasticTermScale(this.dataset.elasticTermScale),
            calculatorJson,
        }

        if (this.dom.formInputs.length === 0) {
            console.warn('No shape inputs found in cut-calculator');
            return;
        }

        if (this.dom.inputFields.length === 0) {
            console.warn('No input fields found in cut-calculator');
            return;
        }
        if (this.dom.shapeInputs.length === 0) {
            console.warn('No shape inputs found in cut-calculator');
            return;
        }

        this.addEventListeners();


        this.handleShapeInputChange();
        this.updatePrice();
        this.syncConfigSnapshot();

        const productForm = document.getElementById(this.dataset.formId);
        if (productForm) {
            productForm.addEventListener('submit', this.syncConfigSnapshot, { capture: true });
        }
    }


    addEventListeners() {
        this.dom.infoTriggers.forEach((trigger) => {
            trigger.addEventListener('click', this.handleInfoTriggerClick);
        });

        this.dom.formInputs.forEach((input) => {
            input.addEventListener('change', (event) => {
                this.inputFieldChange(event);
            })

            input.addEventListener('input', (event) => {
                this.inputFieldChange(event);
            })
        })
    }

    handleInfoTriggerClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        const trigger = event.currentTarget;
        const drawer = document.body.querySelector(`#${ trigger.getAttribute('aria-controls') }`);
        
        if (!drawer) return;

        trigger.setAttribute('aria-expanded', 'true');
        drawer.addEventListener(
            `on:${ drawer.dataset.name }:after-close`,
            () => trigger.setAttribute('aria-expanded', 'false'),
            { once: true }
        );
        drawer.open(trigger);
    };

    inputFieldChange = (event) => {
        if (event.target.classList.contains('cut-calculator__shape-input')) {
            this.settings.currentShape = event.target.value;
        }

        this.handleShapeInputChange();
        this.updatePrice();
        this.syncConfigSnapshot();

        this.setPseudoInput(event.target);
    }

    handleShapeInputChange = () => {
        this.dom.inputFields.forEach((input) => {



            if (this.isElementHidden(input)) {
                input.disabled = true;
            } else {
                input.disabled = false;
            }

            // if the element has a data-pseudo-property-id attribute, set the disabled property on the pseudo input
            if (input.hasAttribute('data-pseudo-property-id')) {
                const pseudoInput = this.querySelector(`input#${ input.dataset.pseudoPropertyId }`);

                if (pseudoInput) {
                    pseudoInput.disabled = input.disabled;
                }

                // If input, that has a data-pseudo-property-id attribute, is also a checkbox, set the checked property on the pseudo input as disabled or not
                if (input.type === 'checkbox' && !input.disabled) {
                    if (pseudoInput) {
                        pseudoInput.disabled = !input.checked;
                    }

                    if (input.id === 'checkbox-buy-whole-roll') {
                        const lengthInput = this.querySelector('input#length');
                        const lenghtPseudoInput = this.querySelector(`input#propertyLength`);
                        if (lengthInput) {
                            lengthInput.disabled = input.checked;
                            lenghtPseudoInput.disabled = input.checked;
                        }
                    }
                }
            }
        })

    }

    getShapeSurcharge = (shape) => {
        const fromJson = lookupJsonShapePrice(this.settings.calculatorJson, shape);
        if (fromJson !== null) {
            return fromJson;
        }

        switch (shape) {
            case 'Rectangular':
            case 'Round with Elastic':
                return 0;
            case 'Oval':
            case 'Ellipse':
            case 'Round':
                return this.settings.basePrice;
            default:
                return 0;
        }
    };

    /**
     * Writes non-price calculator state for the cart transform function
     * (properties[_cut_calculator_config]). Omits keys for disabled or empty fields.
     */
    syncConfigSnapshot = () => {
        const configInputs = [
            this.querySelector('[name="properties[_cut_calculator_config]"]'),
            document.getElementById(`cut-calculator-config-${ this.dataset.formId }`),
        ].filter(Boolean);

        if (configInputs.length === 0) {
            return;
        }

        const snapshot = {};

        const shapeInput = this.querySelector('input.cut-calculator__shape-input:checked');
        if (shapeInput?.value) {
            snapshot.shape = shapeInput.value;
        }

        const readNumericField = (selector) => {
            const input = this.querySelector(selector);
            if (!input || input.disabled) {
                return undefined;
            }
            const value = String(input.value ?? '').trim();
            return value === '' ? undefined : value;
        };

        const length = readNumericField('input#length');
        if (length !== undefined) {
            snapshot.length = length;
        }

        const width = readNumericField('input#width');
        if (width !== undefined) {
            snapshot.width = width;
        }

        const diameter = readNumericField('input#diameter');
        if (diameter !== undefined) {
            snapshot.diameter = diameter;
        }

        const diameterWithElastic = readNumericField('input#diameterWithElastic');
        if (diameterWithElastic !== undefined) {
            snapshot.diameter = diameterWithElastic;
        }

        const thickness = readNumericField('input#thickness');
        if (thickness !== undefined) {
            snapshot.thickness = thickness;
        }

        const wholeRollCheckbox = this.querySelector('input#checkbox-buy-whole-roll');
        if (
            wholeRollCheckbox &&
            wholeRollCheckbox.checked &&
            !wholeRollCheckbox.disabled
        ) {
            const rollValue = String(wholeRollCheckbox.value ?? '').trim();
            if (rollValue !== '') {
                snapshot.buy_whole_roll = rollValue;
            }
        }

        const roundedCornersToggle = this.querySelector(
            'input#checkbox-show-rounded-corners-option',
        );
        const roundedCornersInput = this.querySelector('input#rounded-corners');
        if (
            roundedCornersToggle?.checked &&
            roundedCornersInput &&
            !roundedCornersInput.disabled
        ) {
            const roundedValue = String(roundedCornersInput.value ?? '').trim();
            if (roundedValue !== '') {
                snapshot.rounded_corners = roundedValue;
            }
        }

        const beveledCheckbox = this.querySelector(
            'input#checkbox-show-beveled-edges-option',
        );
        if (beveledCheckbox?.checked && !beveledCheckbox.disabled) {
            const beveledValue = String(beveledCheckbox.value ?? '').trim();
            if (beveledValue !== '') {
                snapshot.beveled_edges = beveledValue;
            }
        }

        const serialized = JSON.stringify(snapshot);
        configInputs.forEach((input) => {
            input.value = serialized;
        });
    };

    isElementHidden = (element) => {
        let currentElement = element;
        while (currentElement) {
            const style = window.getComputedStyle(currentElement);
            if (style.display === 'none') {
                return true;
            }
            currentElement = currentElement.parentElement;
        }
        return false;
    }

    setPseudoInput = (input) => {
        const pseudoInput = this.querySelector(`input#${ input.dataset.pseudoPropertyId }`);
        const pseudoInputName = input.dataset.pseudoPropertyName;
        const pseudoInputUnit = input.dataset.pseudoPropertyUnit;
        let pseudoInputValue = null;

        if (input.hasAttribute('data-pseudo-property-value')) {
            const attributeValue = input.getAttribute('data-pseudo-property-value');
            pseudoInputValue = attributeValue ? attributeValue : input.value;
        }

        if (!pseudoInput) {
            return;
        }
        if (pseudoInputName) {
            pseudoInput.setAttribute('name', `properties[${ pseudoInputName }]`);
        }

        if (pseudoInputValue) {
            const valueWithUnit = pseudoInputUnit ? `${ pseudoInputValue } ${ pseudoInputUnit }` : pseudoInputValue;
            pseudoInput.setAttribute('value', valueWithUnit);
        }
    }

    updatePrice = () => {
        const pricePerCentimeter = this.settings.basePrice / 100;
        const compareAtPricePerCentimeter = this.settings.baseCompareAtPrice / 100;

        let price = this.settings.basePrice;
        let compareAtPrice = this.settings.baseCompareAtPrice;

        switch (this.settings.currentShape) {
            case 'Rectangular':
            case 'Oval':
            case 'Ellipse':

                const lengthInput = this.querySelector('input#length');
                const lengthPseudoInput = this.querySelector(`input#propertyLength`);
                const widthInput = this.querySelector('input#width')
                const wholeRoll = this.querySelector('input#checkbox-buy-whole-roll');

                if (lengthInput && lengthInput.value) {
                    let length = Number(lengthInput.value);


                    if (wholeRoll && wholeRoll.checked && !wholeRoll.disabled) {
                        length = lengthInput.max;
                        lengthInput.disabled = true;
                        lengthPseudoInput.disabled = true;
                    } else {
                        lengthInput.disabled = false;
                        lengthPseudoInput.disabled = false;
                    }


                    price = pricePerCentimeter * length;
                    compareAtPrice = compareAtPricePerCentimeter * length;

                }

                if (wholeRoll && wholeRoll.checked && !wholeRoll.disabled) {
                    length = lengthInput.max;
                    price = pricePerCentimeter * length;
                    compareAtPrice = compareAtPricePerCentimeter * length;

                    price = price * .85;
                    compareAtPrice = compareAtPrice * .85;
                }

                if (widthInput && widthInput.value && widthInput.disabled === false) {
                    const jsonWidth = lookupFeePrice(
                        this.settings.calculatorJson,
                        FEE_JSON_KEYS.widthAdjustment,
                    );
                    const legacyWidthPrice = Number(widthInput.dataset.cutDownPrice);
                    const widthPrice = Number.isFinite(legacyWidthPrice)
                        ? legacyWidthPrice
                        : (jsonWidth !== null ? jsonWidth : 69);
                    price += widthPrice;
                    compareAtPrice += widthPrice;
                }

                const roundedCornersInput = this.querySelector('input#rounded-corners');

                if (roundedCornersInput && roundedCornersInput.value && roundedCornersInput.disabled === false) {
                    const jsonRounded = lookupFeePrice(
                        this.settings.calculatorJson,
                        FEE_JSON_KEYS.roundedCorners,
                    );
                    const roundedCornersPrice = jsonRounded !== null
                        ? jsonRounded
                        : (Number(roundedCornersInput.dataset.roundedCornersPrice) || 69);
                    price += roundedCornersPrice;
                    compareAtPrice += roundedCornersPrice;
                }

                const lengthShapeSurcharge = this.getShapeSurcharge(this.settings.currentShape);
                price += lengthShapeSurcharge;
                compareAtPrice += lengthShapeSurcharge;

                break;

            case 'Round':
                const diameterInput = this.querySelector('input#diameter');
                const roundDiameterInMeters = Number(diameterInput.value / 100) || 0;
                this.setMaxDiameter(diameterInput, this.settings.width - 2);

                const roundSurcharge = this.getShapeSurcharge('Round');
                price = (this.settings.basePrice * roundDiameterInMeters) + roundSurcharge;
                compareAtPrice = (this.settings.baseCompareAtPrice * roundDiameterInMeters) + roundSurcharge;
                break;

            case 'Round with Elastic':
                const diameterWithElasticInput = this.querySelector('input#diameterWithElastic');
                const elasticDiameter = Number(diameterWithElasticInput.value) || 0;
                const thickness = Number(this.querySelector('input#thickness').value) || 0;
                const maxDiameter = this.settings.width - (6 + (2 * thickness));
                const elasticMultiplier = Number(diameterWithElasticInput.dataset.elasticMultiplier) || 1;
                this.setMaxDiameter(diameterWithElasticInput, maxDiameter);
                const elasticTermScale = this.settings.elasticTermScale;
                const roundDiameterWithElasticPriceCalculation = (price, diameter, thickness) => { 

                    if (thickness == 0) {
                        const diameterInMeters = diameter / 100;
                        return (price * diameterInMeters) + price;
                    }

                    const adjusted = diameter + (2 * (thickness - 2));
                    const formulaTerms = (0.034012 * Math.pow(adjusted, 2)) - (4.313974 * adjusted) + 279.994475;
                    return (formulaTerms * elasticTermScale + (1.064613 * price)) * elasticMultiplier;
                }


                price = roundDiameterWithElasticPriceCalculation(this.settings.basePrice, elasticDiameter, thickness);
                compareAtPrice = roundDiameterWithElasticPriceCalculation(this.settings.baseCompareAtPrice, elasticDiameter, thickness);

                const elasticShapeSurcharge = this.getShapeSurcharge('Round with Elastic');
                price += elasticShapeSurcharge;
                compareAtPrice += elasticShapeSurcharge;

                break;
        }

        const beveledEdgesCheckbox = this.querySelector('input#checkbox-show-beveled-edges-option');

        if (beveledEdgesCheckbox && beveledEdgesCheckbox.checked && beveledEdgesCheckbox.disabled === false) {
            const jsonBeveled = lookupFeePrice(
                this.settings.calculatorJson,
                FEE_JSON_KEYS.beveledEdges,
            );
            const beveledEdgesPrice = jsonBeveled !== null
                ? jsonBeveled
                : (Number(beveledEdgesCheckbox.dataset.beveledEdgesPrice) || 0);
            price += beveledEdgesPrice;
            compareAtPrice += beveledEdgesPrice;
        }

        if (this.dom.price) {
            this.dom.price.innerHTML = this.formatPrice(price.toFixed(2), this.settings.currentShape === 'Round with Elastic');
        }

        if (this.dom.compareAtPrice && this.dom.compareAtPrice.innerHTML !== '') {

            if (compareAtPrice > 0) {

                this.dom.compareAtPrice.innerHTML = this.formatPrice(compareAtPrice.toFixed(2), this.settings.currentShape === 'Round with Elastic');
            } else {
                this.dom.compareAtPrice.innerHTML = '';
            }
        }
    }

    setMaxDiameter = (input, maxDiameter) => {
        if (input && maxDiameter) {
            input.setAttribute('max', maxDiameter);

        }
    }

    formatPrice = (price, roundPrice = false) => {

        const newPrice = roundPrice ? Math.round(price).toFixed(2) : price;

        // convert number to price with currency
        const currency = Shopify?.currency?.active || 'USD';


        // Format price based on currency/locale
        let formattedPrice = newPrice;

        // Check for Shopify's money format or use browser's locale
        if (Shopify && Shopify.currency && Shopify.currency.active) {
            // Some currencies use comma as decimal separator
            const commaDecimalCurrencies = ['EUR', 'DKK', 'NOK', 'SEK'];
            if (commaDecimalCurrencies.includes(currency) && newPrice.toString().includes('.')) {
                formattedPrice = newPrice.replace('.', ',');
            }
        } else {
            // Fallback to browser locale
            try {
                const numberFormat = new Intl.NumberFormat(navigator.language, {
                    style: 'decimal',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
                formattedPrice = numberFormat.format(parseFloat(newPrice));
            } catch (e) {
                console.warn('Error formatting price with locale', e);
            }
        }

        return theme.settings.moneyWithCurrencyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, formattedPrice);
    }
}

customElements.define('cut-calculator', CutCalculator);